import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Tag, { TAG_COLORS } from "@shared/ui/Tag";
import React from "react";
import { useIntl } from "react-intl";

import {
  WorkOrderAssetsTooltipContent,
  type WorkOrderAssetTooltipItem,
} from "./WorkOrderAssetsTooltipContent";

export const WORK_ORDER_ASSET_COLUMN_LABELS = new Set(["Machine", "Asset"]);

export type WorkOrderAssetTagOrTextProps = {
  assets?: WorkOrderAssetTooltipItem[];
  machine?: WorkOrderAssetTooltipItem | null;
  display?: "table" | "inline";
  className?: string;
};

export const WorkOrderAssetTagOrText: React.FC<
  WorkOrderAssetTagOrTextProps
> = ({ assets = [], machine = null, display = "table", className = "" }) => {
  const { messages } = useIntl();
  const assetsTabLabel =
    (messages as { connections?: { single?: { tabs?: { assets?: string } } } })
      ?.connections?.single?.tabs?.assets ?? "Assets";
  const deletedAssetLabel =
    (messages as { hierarchy?: { assets?: { deletedAsset?: string } } })
      ?.hierarchy?.assets?.deletedAsset ?? "Deleted asset";
  const noAssetLabel =
    (messages as { tickets?: { noAssetsAssigned?: string } })?.tickets
      ?.noAssetsAssigned ?? "No assets assigned";

  const list = assets ?? [];
  const hasMultiple = list.length > 1;
  const primary = list[0] ?? machine ?? null;

  if (hasMultiple) {
    return (
      <span
        className={`inline-flex max-w-full ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <Tag
          label={`${list.length} ${assetsTabLabel}`}
          color={TAG_COLORS.GREY}
          showTooltip
          allowOverlayHover
          isUpperCase={false}
          isHoverable={display === "table"}
          tooltipBgColor="bg-secondary !p-3xs"
          className="max-w-full truncate"
          Tooltip={() => (
            <div className="flex flex-col gap-xs px-sm py-2xs font-manrope text-xs font-medium text-inverse">
              <WorkOrderAssetsTooltipContent assets={list} />
            </div>
          )}
        />
      </span>
    );
  }

  const singleLine = primary?.name
    ? [primary.name, primary.serialNumber].filter(Boolean).join(" • ")
    : null;

  if (!singleLine) {
    return (
      <BodyText
        size={BODY_TEXT_SIZES.X_SMALL}
        className={`max-w-full truncate ${className}`}
      >
        {primary?.isDeleted ? deletedAssetLabel : noAssetLabel}
      </BodyText>
    );
  }

  return (
    <BodyText
      size={BODY_TEXT_SIZES.X_SMALL}
      className={`max-w-full truncate ${className}`}
    >
      {singleLine}
    </BodyText>
  );
};
